const HOME_SNAPSHOT_VERSION = 1;
const HOME_SNAPSHOT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const HOME_SNAPSHOT_PREFIX = `tribelog.homeSnapshot.v${HOME_SNAPSHOT_VERSION}:`;
const ACTIVE_HOME_SNAPSHOT_ACCOUNT_KEY = 'tribelog.homeSnapshot.activeAccount';

function browserStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeString(value, maxLength = 160) {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, maxLength);
}

function safeNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function safeBoolean(value) {
  return value === true;
}

function snapshotKey(uid) {
  const normalized = safeString(uid, 160);
  return normalized ? `${HOME_SNAPSHOT_PREFIX}${normalized}` : '';
}

function sanitizeActivity(activity = {}, dateKey = '') {
  const type = safeString(activity.type || activity.activityId, 40);
  if (!type) return null;

  return {
    id: safeString(activity.id, 120),
    type,
    activityId: safeString(activity.activityId || type, 40),
    loggedAt: safeString(activity.loggedAt || dateKey, 16),
    points: Math.max(0, safeNumber(activity.points)),
    value: safeNumber(activity.value),
    recoveredByPro: safeBoolean(activity.recoveredByPro),
    scoringVersion: safeString(activity.scoringVersion, 32),
  };
}

function sanitizeHistory(history = {}) {
  if (!history || typeof history !== 'object' || Array.isArray(history)) return {};

  return Object.entries(history)
    .sort(([leftDate], [rightDate]) => leftDate.localeCompare(rightDate))
    .slice(-120)
    .reduce((result, [dateKey, entry]) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey) || !entry || typeof entry !== 'object') {
        return result;
      }
      const sourceActivities = Array.isArray(entry.activities)
        ? entry.activities
        : entry.type
          ? [entry]
          : [];
      const activities = sourceActivities
        .map(activity => sanitizeActivity(activity, dateKey))
        .filter(Boolean);

      return {
        ...result,
        [dateKey]: {
          activities,
          date: dateKey,
          points: Math.max(0, safeNumber(entry.points ?? entry.totalPoints)),
          totalPoints: Math.max(0, safeNumber(entry.totalPoints ?? entry.points)),
        },
      };
    }, {});
}

function sanitizeCoachToday(source) {
  if (!source || typeof source !== 'object') return null;
  return {
    title: safeString(source.title, 80),
    hasPlannedSession: safeBoolean(source.hasPlannedSession || source.planned),
    completedToday: safeBoolean(source.completedToday || source.completed || source.loggedToday),
    restDay: safeBoolean(source.restDay || source.freeDay || source.noSessionPlanned),
  };
}

function sanitizeUserProfile(profile) {
  if (!profile || typeof profile !== 'object') return null;
  const hasExplicitOnboardingState = Object.prototype.hasOwnProperty.call(
    profile,
    'onboardingDone',
  );

  const weeklyTarget = safeNumber(profile?.goals?.weeklyActiveDaysTarget);
  const coachFrequency = safeNumber(
    profile?.coachSetup?.frequencyDaysPerWeek
      ?? profile?.coach?.frequencyDaysPerWeek
      ?? profile?.workoutCoach?.frequencyDaysPerWeek
      ?? profile?.workoutCoachSetup?.frequencyDaysPerWeek,
  );

  return {
    onboardingDone: profile.onboardingDone === true
      || (!hasExplicitOnboardingState && Boolean(profile.onboarding)),
    avatarEmoji: safeString(profile.avatarEmoji, 8),
    avatarColor: safeString(profile.avatarColor, 24),
    stats: {
      challengesCompleted: Math.max(0, safeNumber(profile?.stats?.challengesCompleted)),
      referralJoins: Math.max(0, safeNumber(profile?.stats?.referralJoins)),
    },
    goals: weeklyTarget > 0 ? { weeklyActiveDaysTarget: weeklyTarget } : {},
    onboarding: {
      frequency: safeString(profile?.onboarding?.frequency, 32),
    },
    coachSetup: coachFrequency > 0 ? { frequencyDaysPerWeek: coachFrequency } : {},
    coachToday: sanitizeCoachToday(
      profile.coachToday
        || profile.todayCoachPlan
        || profile.emberPlanToday,
    ),
    homeState: {
      weeklyRecapViewedWeekKey: safeString(
        profile?.homeState?.weeklyRecapViewedWeekKey
          || profile.weeklyRecapViewedWeekKey
          || profile.lastWeeklyRecapViewedWeekKey,
        16,
      ),
    },
    weeklyRecap: {
      readyWeekKey: safeString(profile?.weeklyRecap?.readyWeekKey, 16),
      sessions: Math.max(0, safeNumber(profile?.weeklyRecap?.sessions)),
      viewedWeekKey: safeString(profile?.weeklyRecap?.viewedWeekKey, 16),
    },
    weeklyRecapReadyWeekKey: safeString(
      profile.weeklyRecapReadyWeekKey || profile.lastWeeklyRecapReadyWeekKey,
      16,
    ),
    sharePreferences: {
      templateId: safeString(profile?.sharePreferences?.templateId, 32),
    },
    cosmetics: {
      profileFrameId: safeString(profile?.cosmetics?.profileFrameId, 32),
    },
  };
}

function sanitizeChallenge(challenge = {}) {
  const id = safeString(challenge.id, 160);
  if (!id) return null;
  return {
    id,
    name: safeString(challenge.name || challenge.title, 100),
    title: safeString(challenge.title || challenge.name, 100),
    emoji: safeString(challenge.emoji, 8),
    color: safeString(challenge.color, 24),
    status: safeString(challenge.status, 32),
    startDate: safeString(challenge.startDate, 16),
    endDate: safeString(challenge.endDate, 16),
    duration: Math.max(0, safeNumber(challenge.duration)),
    memberCount: Math.max(0, safeNumber(challenge.memberCount)),
    isPublic: challenge.isPublic !== false,
  };
}

function sanitizeChallengeStats(stats = {}) {
  return {
    joined: Math.max(0, safeNumber(stats.joined)),
    owned: Math.max(0, safeNumber(stats.owned)),
    completed: Math.max(0, safeNumber(stats.completed)),
    top1: Math.max(0, safeNumber(stats.top1)),
  };
}

function sanitizeHomeSnapshot(data = {}) {
  const hasChallengeDetailsFlag = Object.prototype.hasOwnProperty.call(
    data,
    'challengeDetailsComplete',
  );
  return {
    myHistory: sanitizeHistory(data.myHistory),
    myChallenges: (Array.isArray(data.myChallenges) ? data.myChallenges : [])
      .map(sanitizeChallenge)
      .filter(Boolean),
    challengeDetailsComplete: hasChallengeDetailsFlag
      ? safeBoolean(data.challengeDetailsComplete)
      : true,
    challengeStats: sanitizeChallengeStats(data.challengeStats),
    userProfile: sanitizeUserProfile(data.userProfile),
  };
}

function removeStorageItem(storage, key) {
  try {
    storage?.removeItem(key);
  } catch {
    // Storage cleanup must never block auth or Home.
  }
}

function prepareHomeSnapshotAccount(uid, storage = browserStorage()) {
  if (!storage || !uid) return;
  try {
    const activeUid = storage.getItem(ACTIVE_HOME_SNAPSHOT_ACCOUNT_KEY);
    if (activeUid && activeUid !== uid) {
      removeStorageItem(storage, snapshotKey(activeUid));
      removeStorageItem(storage, `onboarding_${activeUid}`);
    }
    storage.setItem(ACTIVE_HOME_SNAPSHOT_ACCOUNT_KEY, uid);
  } catch {
    // Safari private browsing and embedded webviews can reject storage.
  }
}

function writeHomeSnapshot(uid, data, {
  now = Date.now(),
  storage = browserStorage(),
} = {}) {
  const key = snapshotKey(uid);
  if (!storage || !key) return false;
  prepareHomeSnapshotAccount(uid, storage);

  try {
    storage.setItem(key, JSON.stringify({
      version: HOME_SNAPSHOT_VERSION,
      capturedAt: now,
      data: sanitizeHomeSnapshot(data),
    }));
    return true;
  } catch {
    return false;
  }
}

function readHomeSnapshot(uid, {
  maxAgeMs = HOME_SNAPSHOT_MAX_AGE_MS,
  now = Date.now(),
  storage = browserStorage(),
} = {}) {
  const key = snapshotKey(uid);
  if (!storage || !key) return null;
  prepareHomeSnapshotAccount(uid, storage);

  try {
    const parsed = JSON.parse(storage.getItem(key) || 'null');
    const capturedAt = Number(parsed?.capturedAt);
    if (
      parsed?.version !== HOME_SNAPSHOT_VERSION
      || !Number.isFinite(capturedAt)
      || capturedAt > now + 60 * 1000
      || now - capturedAt > maxAgeMs
      || !parsed.data
    ) {
      removeStorageItem(storage, key);
      return null;
    }
    return {
      capturedAt,
      ...sanitizeHomeSnapshot(parsed.data),
    };
  } catch {
    removeStorageItem(storage, key);
    return null;
  }
}

function clearHomeSnapshot(uid, storage = browserStorage()) {
  if (!storage || !uid) return;
  removeStorageItem(storage, snapshotKey(uid));
  removeStorageItem(storage, `onboarding_${uid}`);
  try {
    if (storage.getItem(ACTIVE_HOME_SNAPSHOT_ACCOUNT_KEY) === uid) {
      storage.removeItem(ACTIVE_HOME_SNAPSHOT_ACCOUNT_KEY);
    }
  } catch {
    // Best-effort privacy cleanup.
  }
}

function clearActiveHomeSnapshot(storage = browserStorage()) {
  if (!storage) return;
  try {
    const uid = storage.getItem(ACTIVE_HOME_SNAPSHOT_ACCOUNT_KEY);
    if (uid) clearHomeSnapshot(uid, storage);
    else storage.removeItem(ACTIVE_HOME_SNAPSHOT_ACCOUNT_KEY);
  } catch {
    // Best-effort privacy cleanup.
  }
}

export {
  ACTIVE_HOME_SNAPSHOT_ACCOUNT_KEY,
  HOME_SNAPSHOT_MAX_AGE_MS,
  HOME_SNAPSHOT_VERSION,
  clearActiveHomeSnapshot,
  clearHomeSnapshot,
  prepareHomeSnapshotAccount,
  readHomeSnapshot,
  sanitizeHomeSnapshot,
  writeHomeSnapshot,
};
